import { audio } from './engine';

type Env = { a?: number; d: number; peak?: number };

function env(g: GainNode, t: number, { a = 0.004, d, peak = 1 }: Env) {
  g.gain.setValueAtTime(0.0001, t);
  g.gain.exponentialRampToValueAtTime(peak, t + a);
  g.gain.exponentialRampToValueAtTime(0.0001, t + a + d);
}

function tone(freq: number, t: number, e: Env, type: OscillatorType = 'sine', dest?: AudioNode, detune = 0) {
  const ctx = audio.ctx!;
  const o = ctx.createOscillator();
  const g = ctx.createGain();
  o.type = type;
  o.frequency.setValueAtTime(freq, t);
  o.detune.value = detune;
  env(g, t, e);
  o.connect(g).connect(dest ?? audio.sfx);
  o.start(t);
  o.stop(t + (e.a ?? 0.004) + e.d + 0.05);
  return o;
}

function noiseBurst(t: number, e: Env, filter: BiquadFilterType, freq: number, q = 1, sweepTo?: number) {
  const ctx = audio.ctx!;
  const src = ctx.createBufferSource();
  src.buffer = audio.noise();
  const f = ctx.createBiquadFilter();
  f.type = filter;
  f.frequency.setValueAtTime(freq, t);
  if (sweepTo) f.frequency.exponentialRampToValueAtTime(sweepTo, t + (e.a ?? 0) + e.d);
  f.Q.value = q;
  const g = ctx.createGain();
  env(g, t, e);
  src.connect(f).connect(g).connect(audio.sfx);
  src.start(t, Math.random() * 0.5);
  src.stop(t + (e.a ?? 0.004) + e.d + 0.05);
}

const now = () => audio.ctx!.currentTime + 0.01;
const guard = (fn: () => void) => () => {
  if (audio.ready) fn();
};

export const sfx = {
  /** Selección de carta: roce breve de papel. */
  select: guard(() => noiseBurst(now(), { d: 0.07, peak: 0.25 }, 'bandpass', 2600, 1.2, 4200)),
  /** Confirmar jugada: golpe suave de madera. */
  confirm: guard(() => {
    const t = now();
    tone(210, t, { d: 0.12, peak: 0.35 }, 'sine');
    noiseBurst(t, { d: 0.05, peak: 0.12 }, 'lowpass', 1200);
  }),
  /** Carta colocada en la ciudad. */
  build: guard(() => {
    const t = now();
    noiseBurst(t, { a: 0.01, d: 0.16, peak: 0.3 }, 'bandpass', 1800, 0.8, 600);
    tone(120, t + 0.06, { d: 0.18, peak: 0.45 }, 'sine');
  }),
  /** Venta / descarte: barrido de aire. */
  discard: guard(() => noiseBurst(now(), { a: 0.02, d: 0.25, peak: 0.22 }, 'bandpass', 900, 0.7, 3500)),
  /** Monedas tintineando (n golpes). */
  coins: (n = 3) => {
    if (!audio.ready) return;
    const t0 = now();
    for (let i = 0; i < Math.min(n, 8); i++) {
      const t = t0 + i * (0.055 + Math.random() * 0.03);
      const f = 2300 + Math.random() * 500;
      tone(f, t, { d: 0.22, peak: 0.12 }, 'sine');
      tone(f * 1.52, t, { d: 0.14, peak: 0.07 }, 'sine');
      tone(f * 2.37, t, { d: 0.08, peak: 0.04 }, 'triangle');
    }
  },
  /** Etapa de maravilla: piedra profunda + campanas. */
  wonder: guard(() => {
    const t = now();
    tone(55, t, { a: 0.01, d: 0.9, peak: 0.6 }, 'sine');
    noiseBurst(t, { a: 0.01, d: 0.5, peak: 0.25 }, 'lowpass', 400);
    [523.25, 659.25, 783.99, 1046.5].forEach((f, i) => tone(f, t + 0.18 + i * 0.09, { a: 0.01, d: 1.6, peak: 0.12 }, 'sine'));
  }),
  /** Tambores de guerra (victoria o derrota). */
  battle: (won: boolean) => {
    if (!audio.ready) return;
    const t = now();
    [0, 0.22, 0.44, 0.58].forEach((dt, i) => {
      const ctx = audio.ctx!;
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.frequency.setValueAtTime(150, t + dt);
      o.frequency.exponentialRampToValueAtTime(48, t + dt + 0.25);
      env(g, t + dt, { a: 0.003, d: 0.35, peak: i === 3 ? 0.9 : 0.6 });
      o.connect(g).connect(audio.sfx);
      o.start(t + dt);
      o.stop(t + dt + 0.5);
      noiseBurst(t + dt, { d: 0.12, peak: 0.25 }, 'lowpass', 900);
    });
    const chord = won ? [293.66, 369.99, 440] : [293.66, 349.23, 415.3];
    chord.forEach((f) => tone(f, t + 0.85, { a: 0.08, d: 1.4, peak: 0.12 }, 'sawtooth', undefined, Math.random() * 8));
  },
  /** Cuerno de inicio de era. */
  horn: guard(() => {
    const t = now();
    const ctx = audio.ctx!;
    const f = ctx.createBiquadFilter();
    f.type = 'lowpass';
    f.frequency.setValueAtTime(300, t);
    f.frequency.linearRampToValueAtTime(1600, t + 0.6);
    f.frequency.linearRampToValueAtTime(700, t + 1.8);
    f.connect(audio.sfx);
    [146.83, 220, 293.66].forEach((fr, i) => tone(fr, t + i * 0.02, { a: 0.25, d: 1.6, peak: 0.18 }, 'sawtooth', f, Math.random() * 10 - 5));
  }),
  /** Fanfarria de final. */
  fanfare: guard(() => {
    const t = now();
    [293.66, 369.99, 440, 587.33, 739.99, 880].forEach((f, i) =>
      tone(f, t + i * 0.12, { a: 0.02, d: 0.9, peak: 0.13 }, i % 2 ? 'triangle' : 'sawtooth'),
    );
  }),
  /** ¡Te toca!: dos notas de lira en quinta ascendente (llega tu nueva mano). */
  yourTurn: guard(() => {
    const t = now();
    [587.33, 880].forEach((f, i) => {
      tone(f, t + i * 0.14, { a: 0.005, d: 0.55, peak: 0.2 }, 'triangle');
      tone(f * 2, t + i * 0.14, { a: 0.005, d: 0.3, peak: 0.05 }, 'sine');
    });
  }),
  /** Campanilla suave (chat, recordatorio). */
  bell: guard(() => {
    const t = now();
    tone(1318.5, t, { d: 0.8, peak: 0.12 }, 'sine');
    tone(1975.5, t, { d: 0.5, peak: 0.05 }, 'sine');
  }),
  pop: guard(() => {
    const t = now();
    const ctx = audio.ctx!;
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.frequency.setValueAtTime(400, t);
    o.frequency.exponentialRampToValueAtTime(900, t + 0.08);
    env(g, t, { d: 0.12, peak: 0.25 });
    o.connect(g).connect(audio.sfx);
    o.start(t);
    o.stop(t + 0.2);
  }),
};
